import { ReactElement, useCallback, useState } from 'react';
import { UIButton, UIErrorState, UILoadingState, UISubNav } from '@/ui';
import { Navigate, Outlet, useParams } from 'react-router';
import { TeamProvider, useTeam } from '@/companies/providers';
import { useFetchTeam, useFetchTeamCounts } from '@/companies/queries';
import { useTeamPermissions } from '@/companies/hooks';
import { usePagesTranslation } from '../../hooks';
import { AddTeamUsersModal } from '@/companies/components/add-team-users-modal';

export function TeamIdSettingsPage(): ReactElement {
    const { teamId } = useParams();

    // Fetched by id rather than looked up in a list: this page is reachable by
    // deep link, bookmark and refresh, and the teams list is a page that may
    // not hold this team.
    const { team, status, error, canRetry, isNotFound, refetch } = useFetchTeam(
        teamId ?? ''
    );

    if (!teamId) {
        return <Navigate to="/" replace />;
    }

    if (status === 'pending') {
        return <PageState>{<UILoadingState />}</PageState>;
    }

    if (status === 'error' || !team) {
        // Gone, archived, or not visible to me — anything else is worth a retry
        // rather than silently bouncing the user to the dashboard.
        if (isNotFound) {
            return <Navigate to="/" replace />;
        }

        return (
            <PageState>
                <UIErrorState
                    title={error}
                    onRetry={canRetry ? refetch : undefined}
                />
            </PageState>
        );
    }

    return (
        <TeamProvider team={team}>
            <TeamIdSettingsPage2 />
        </TeamProvider>
    );
}

function PageState(props: { children: ReactElement }): ReactElement {
    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                padding: 32
            }}
        >
            {props.children}
        </div>
    );
}

function TeamIdSettingsPage2(): ReactElement {
    const { t } = usePagesTranslation();
    const { team } = useTeam();
    const { canViewTeam, canEditTeam } = useTeamPermissions({
        teamId: team.id
    });

    // Counted by the server, company-wide: the same call the teams list uses,
    // so arriving from there costs nothing.
    const { counts } = useFetchTeamCounts(team.companyId);
    const memberCount = counts?.teams[team.id];
    // Nothing to say until the counts land — better than flashing "0 members".
    const subtitle =
        memberCount === undefined
            ? undefined
            : t('pages.team_id_settings.member_count', { count: memberCount });

    const [isAddTeamUsersModalOpen, setIsAddTeamUsersModalOpen] =
        useState(false);

    const rightNode = useCallback(() => {
        if (!canEditTeam) {
            return undefined;
        }
        return (
            <UIButton
                variant="primary"
                text={t('pages.members_settings.add')}
                leftIcon={'plus-circle'}
                onClick={() => {
                    setIsAddTeamUsersModalOpen(true);
                }}
            />
        );
    }, [canEditTeam, t]);

    if (!canViewTeam) {
        return <Navigate to="/" replace />;
    }

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    padding: 32,
                    gap: 24
                }}
            >
                <UISubNav
                    title={team.name}
                    subTitle={subtitle}
                    img={{
                        img: team.image,
                        alt: team.name
                    }}
                    rightItems={rightNode()}
                    links={[
                        {
                            text: t('shared.labels.general'),
                            link: 'general',
                            additionalActiveLinkPaths: [
                                `/company/${team.companyId}/settings/teams/${team.id}`
                            ]
                        },
                        {
                            text: t('shared.labels.members'),
                            link: 'members'
                        },
                        {
                            text: t('shared.labels.devices'),
                            link: 'devices'
                        },
                        {
                            text: t('shared.labels.notifications'),
                            link: 'notifications'
                        }
                    ]}
                />
                {<Outlet />}
            </div>
            <AddTeamUsersModal
                teamId={team.id}
                isOpen={isAddTeamUsersModalOpen}
                onClose={() => setIsAddTeamUsersModalOpen(false)}
            />
        </>
    );
}
